import { DatePicker, Tag } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { TagsContainer, Wrapper } from './SpecificDaysPicker.styles';

const { RangePicker } = DatePicker;

type Props = {
  value?: Date[];
  onChange?: (value: Date[]) => void;
  hideLabel?: boolean;
};

const expandRange = (start: Dayjs, end: Dayjs): Date[] => {
  const dates: Date[] = [];
  let current = start.startOf('day');
  const endDay = end.startOf('day');

  while (current.isBefore(endDay) || current.isSame(endDay, 'day')) {
    dates.push(current.toDate());
    current = current.add(1, 'day');
  }

  return dates;
};

const deduplicateDates = (dates: Date[]): Date[] => {
  const seen = new Set<string>();
  return dates.filter(date => {
    const key = dayjs(date).format('YYYY-MM-DD');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const SpecificDaysPicker: React.FC<Props> = ({ value, onChange, hideLabel }) => {
  const { t } = useTranslation('DefaultTranslations');

  const handleRangeChange = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      if (!dates || !dates[0] || !dates[1]) return;

      const newDates = expandRange(dates[0], dates[1]);
      const merged = deduplicateDates([...(value ?? []), ...newDates]);

      onChange?.(merged);
    },
    [value, onChange]
  );

  const handleRemoveDate = useCallback(
    (dateToRemove: Date) => {
      const keyToRemove = dayjs(dateToRemove).format('YYYY-MM-DD');
      const filtered = (value ?? []).filter(
        date => dayjs(date).format('YYYY-MM-DD') !== keyToRemove
      );
      onChange?.(filtered);
    },
    [value, onChange]
  );

  const sortedDates = [...(value ?? [])].sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());

  return (
    <Wrapper>
      {!hideLabel && <p>{t('SPECIFIC_DAYS')}</p>}
      <RangePicker
        format="DD/MM/YYYY"
        minDate={dayjs().startOf('day')}
        value={null}
        onChange={handleRangeChange}
        style={{ width: '100%' }}
      />
      {sortedDates.length > 0 && (
        <TagsContainer>
          {sortedDates.map(date => (
            <Tag
              key={dayjs(date).format('YYYY-MM-DD')}
              closable
              onClose={() => handleRemoveDate(date)}>
              {dayjs(date).format('DD/MM/YYYY')}
            </Tag>
          ))}
        </TagsContainer>
      )}
    </Wrapper>
  );
};
